import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import Logo from '../../components/Logo';
import { useToast } from '../../hooks/use-toast';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassphraseOption, setShowPassphraseOption] = useState(false);
  const [passphrase, setPassphrase] = useState('');

  // Check if already logged in via passphrase
  React.useEffect(() => {
    const passphraseAccess = localStorage.getItem('passphrase_access');
    const adminToken = localStorage.getItem('admin_token');
    
    if (passphraseAccess === 'true' && adminToken) {
      navigate('/admin-portal/dashboard');
    }
  }, [navigate]);

  const handlePassphraseLogin = async (e) => {
    e.preventDefault();
    
    const SECRET_PASSPHRASE = 'EDGETRADE_SECURE_2024';
    
    if (passphrase !== SECRET_PASSPHRASE) {
      toast({
        title: 'Invalid Passphrase',
        description: 'The security passphrase is incorrect',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const BACKEND_URL = '';
      
      // Auto-login with passphrase
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: 'admin',  // Use admin username
          password: 'admin123'
        })
      });

      if (!response.ok) {
        toast({
          title: 'Error',
          description: 'System authentication failed. Firebase quota may be exceeded.',
          variant: 'destructive'
        });
        return;
      }

      const data = await response.json();
      
      if (data.access_token || data.token) {
        const token = data.access_token || data.token;
        localStorage.setItem('admin_token', token);
        localStorage.setItem('token', token);
        if (data.admin) {
          localStorage.setItem('admin_user', JSON.stringify(data.admin));
        }
        localStorage.setItem('passphrase_access', 'true');
        
        toast({
          title: 'Success',
          description: 'Access granted via passphrase'
        });

        navigate('/admin-portal/dashboard');
      } else {
        toast({
          title: 'Error',
          description: 'Invalid response from server',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Passphrase login error:', error);
      toast({
        title: 'Error',
        description: 'Failed to connect to server',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      alert('Please enter both username and password');
      return;
    }

    setLoading(true);

    try {
      const BACKEND_URL = '';
      
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: username,  // Backend expects 'username' field, not 'email'
          password: password
        })
      });

      if (!response.ok) {
        // Handle non-ok responses
        let errorMessage = 'Invalid credentials';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || 'Login failed';
        }
        
        toast({
          title: 'Login Failed',
          description: errorMessage,
          variant: 'destructive'
        });
        return;
      }

      const data = await response.json();

      if (data.access_token || data.token) {
        // Store admin token
        const token = data.access_token || data.token;
        localStorage.setItem('admin_token', token);
        localStorage.setItem('token', token);  // Also store as regular token
        if (data.admin) {
          localStorage.setItem('admin_user', JSON.stringify(data.admin));
        }
        
        toast({
          title: 'Success',
          description: 'Admin login successful'
        });
        
        // Redirect to admin dashboard
        navigate('/admin-portal/dashboard');
      } else {
        toast({
          title: 'Error',
          description: 'Invalid response from server',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Admin login error:', error);
      toast({
        title: 'Connection Error',
        description: 'Failed to connect to server. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Logo size="large" showText={true} />
        </div>

        {/* Login Card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-cyan-500/30 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-500 to-green-500 flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Portal</h1>
            <p className="text-cyan-400">Authorized Access Only</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            {/* Username Input */}
            <div>
              <label className="block text-cyan-400 text-sm font-semibold mb-2">
                Admin Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-600" />
                <input
                  type="text"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin.master"
                  autoComplete="username"
                  className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-800 focus:outline-none focus:border-cyan-400 transition-colors"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-cyan-400 text-sm font-semibold mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-600" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  autoComplete="current-password"
                  className="w-full pl-10 pr-12 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-800 focus:outline-none focus:border-cyan-400 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-cyan-600 hover:text-cyan-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-green-500 hover:from-cyan-400 hover:to-green-400 text-white font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Authenticating...
                </div>
              ) : (
                'Access Admin Panel'
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-xs text-center">
              ⚠️ Unauthorized access is strictly prohibited and monitored
            </p>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-cyan-400 hover:text-cyan-300 text-sm"
          >
            ← Back to Main Site
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
